import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { MeetingsOverviewMeetingFragment } from "types/graphql-schema";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import { meetingDialogAction } from "@apps/meeting-dialog/meeting-dialog-constants";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import MotionDiv from "@components/motion/motion-div";
import { classNames } from "@helpers/css";
import { getUrl, isTailwindLgActive } from "@helpers/helpers";

const OverviewMeeting = ({
  meeting,
  onMeetingUpdated,
  onClickMeetingLink,
}: {
  meeting: MeetingsOverviewMeetingFragment;
  onMeetingUpdated: (data: {
    meeting: MeetingsOverviewMeetingFragment;
  }) => void;
  hideEditingButtons?: boolean;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const link = useLink();
  const [isShowingMeetingDialog, setIsShowingMeetingDialog] = useState(false);

  const isClickable = meeting.canRead?.permission;

  const meetingUrl = useMemo(
    () =>
      getUrl({
        meetingGroupId: meeting.meetingGroupId,
        meetingId: meeting.id,
      }),
    [meeting],
  );

  const handleCloseMeetingDialog = useCallback(() => {
    setIsShowingMeetingDialog(false);
  }, []);

  const handleGoToMeeting = useCallback(() => {
    link.redirect(meetingUrl);
  }, [link, meetingUrl]);

  const handleClickMeetingLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
      if (onClickMeetingLink && meeting.meetingGroup?.id) {
        if (isTailwindLgActive()) {
          e.stopPropagation();
          e.preventDefault();
          onClickMeetingLink(meeting.id, meeting.meetingGroup.id);
        } else {
          handleGoToMeeting();
        }
      }
    },
    [
      meeting.id,
      onClickMeetingLink,
      meeting.meetingGroup?.id,
      handleGoToMeeting,
    ],
  );

  return (
    <>
      {isShowingMeetingDialog && (
        <MeetingDialog
          meetingId={meeting.id}
          meetingGroupId={meeting.meetingGroup?.id}
          onClose={handleCloseMeetingDialog}
          defaultAction={meetingDialogAction.edit}
          onSaved={onMeetingUpdated}
        />
      )}
      <MotionDiv
        className="text-sm text-gray-600 rounded-md px-2 -mx-2 py-1 hover:bg-gray-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        role={isClickable ? "button" : undefined}
        onClick={
          isClickable && onClickMeetingLink
            ? handleClickMeetingLink
            : isClickable && !onClickMeetingLink
              ? handleGoToMeeting
              : undefined
        }
      >
        {/* <div className="flex items-center justify-between"> */}
        <AppLink
          className={classNames("flex items-center gap-1 w-full min-w-0")}
          to={meetingUrl}
          disabled={!isClickable}
          onClick={handleClickMeetingLink}
        >
          <span className="flex-1 truncated">{meeting.title}</span>
          <span className="text-gray-500 text-xs">
            {`${moment(meeting.startDatetime)
              .format("h:mma")
              .replace(":00", "")}`}
          </span>
        </AppLink>
      </MotionDiv>
    </>
  );
};

export default OverviewMeeting;
